import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker } from "react-icons/hi";
import { FaWhatsapp, FaFacebookF, FaInstagram, FaTiktok } from "react-icons/fa";

const Footer = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <footer className="bg-[#0F0F0F] text-gray-400 relative overflow-hidden pt-32 pb-6">

      {/* LÍNEA SUPERIOR EN DEGRADADO ANIMADO */}
      <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#F26B1D] via-orange-400 to-[#F26B1D] bg-[length:200%_auto] animate-gradient-x" />

      {/* Grid técnico de fondo */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(#F26B1D 1px, transparent 1px)', backgroundSize: '30px 30px' }}
      />

      <motion.div
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-16 relative z-10"
      >
        {/* COL 1: LOGO & BIO */}
        <motion.div variants={item} className="space-y-6">
          <h2 className="text-3xl font-black text-white tracking-tighter">
            PLA<span className="text-[#F26B1D]">NIX</span>
          </h2>
          <p className="text-sm leading-relaxed text-gray-500">
            Expertos en la formalización de la propiedad privada. Soluciones de ingeniería y asesoría legal estratégica para el saneamiento físico legal en todo el Perú.
          </p>
          <div className="flex gap-3">
            {[
              { icon: <FaWhatsapp />, href: "https://wa.me/51983974833", color: "hover:bg-[#25D366]" },
              { icon: <FaInstagram />, href: "#", color: "hover:bg-[#E1306C]" },
              { icon: <FaFacebookF />, href: "#", color: "hover:bg-[#1877F2]" },
              { icon: <FaTiktok />, href: "#", color: "hover:bg-white hover:text-black" }
            ].map((social, i) => (
              <motion.a
                key={i}
                href={social.href}
                whileHover={{ y: -3 }}
                className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white transition-all duration-300 ${social.color}`}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* COL 2: NAVEGACIÓN */}
        <motion.div variants={item}>
          <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-8">Explorar</h4>
          <ul className="grid grid-cols-1 gap-4">
            {["Inicio", "Servicios", "Planos", "Nosotros", "Contacto"].map((name) => (
              <li key={name}>
                <Link
                  to={name === "Inicio" ? "/" : `/${name.toLowerCase()}`}
                  className="text-sm hover:text-[#F26B1D] transition-colors duration-300 flex items-center gap-2 group"
                >
                  <span className="w-0 h-[1px] bg-[#F26B1D] group-hover:w-4 transition-all duration-300" />
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* COL 3: SERVICIOS TÉCNICOS */}
        <motion.div variants={item}>
          <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-8">Especialidades</h4>
          <ul className="space-y-4 text-sm">
            {[
              "Saneamiento Físico Legal",
              "Independización / Subdivisión",
              "Declaratoria de Fábrica",
              "Levantamientos Topográficos",
              "Diseño de Planos"
            ].map((service) => (
              <li key={service} className="text-gray-500 hover:text-white transition-colors cursor-default">
                {service}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* COL 4: CONTACTO DIRECTO */}
        <motion.div variants={item} className="space-y-6">
          <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-8">Central de Consultas</h4>
          <div className="space-y-4 text-sm">
            <a href="tel:+51920238477" className="flex items-center gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-[#F26B1D] group-hover:bg-[#F26B1D] group-hover:text-white transition-all">
                <HiOutlinePhone size={20} />
              </div>
              <span>+51 983 974 833</span>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-[#F26B1D]">
                <HiOutlineMail size={20} />
              </div>
              <span>contacto@planix.pe</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-[#F26B1D]">
                <HiOutlineLocationMarker size={20} />
              </div>
              <span>Lima, Perú</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* FOOTER BOTTOM: COPYRIGHT Y PORTAFOLIO */}
      <div className="mt-20 border-t border-white/5 pt-8 px-6 flex flex-col md:flex-row justify-between items-center gap-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600">
          © {new Date().getFullYear()} <span className="text-gray-400 font-bold">Planix</span>. Todos los derechos reservados.
        </p>

        {/* CRÉDITOS: TU MARCA PERSONAL */}
        <motion.a
          href="https://ajgeo.netlify.app/" 
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          className="group relative"
        >
          {/* Efecto de resplandor suave detrás */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-[#F26B1D] to-orange-400 rounded-full blur opacity-10 group-hover:opacity-40 transition duration-500"></div>

          <div className="relative bg-[#1C1C1C] border border-white/10 px-5 py-2 rounded-full flex items-center gap-3 group-hover:border-[#F26B1D]/50 transition-all duration-300">
            {/*<span className="text-[9px] uppercase font-black text-gray-500 tracking-tighter">Hecho con</span>*/}

            {/* Iconos de programación y pasión 
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#F26B1D] animate-pulse">❤️</span>
              <span className="text-[10px] text-gray-600 font-mono group-hover:text-white transition-colors">{"</>"}</span>
            </div>*/}

            <span className="h-3 w-[1px] bg-white/10" />

            {/* Tu Nombre con Degradado 
            <a href="https://ajgeo.netlify.app/" // <--- Aquí va tu link
              target="_blank"
              rel="noopener noreferrer"
              className="group-hover:underline"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-orange-100 to-[#F26B1D] font-black text-[11px] uppercase tracking-[0.2em] group-hover:from-[#F26B1D] group-hover:to-white transition-all duration-500">
                AJ.geo
              </span>
            </a>*/}
          </div>
          
        </motion.a>
      </div>
    </footer>
  );
};

export default Footer;